/**
 * 从四边形 box 取行内排序用坐标：左边界 x、纵向中心 y
 * @param {number[][]|undefined} box [[x,y],...]
 */
function ocrBoxXY(box) {
    if (!Array.isArray(box) || box.length === 0) return { x: 0, y: 0 }
    let minX = Infinity
    let sumY = 0
    let n = 0
    for (const p of box) {
        if (Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
            minX = Math.min(minX, p[0])
            sumY += p[1]
            n += 1
        }
    }
    if (n === 0) return { x: 0, y: 0 }
    return { x: minX === Infinity ? 0 : minX, y: sumY / n }
}

/**
 * 简析 Umi-OCR 等返回的框列表：按版面聚类成行，行内从左到右拼接，得到接近阅读顺序的纯文本。
 * @param {Array<{ box?: number[][], text?: string, end?: string, score?: number }>} blocks
 * @param {{ yThreshold?: number }} [opts] 同一行允许的纵向像素偏差，默认 18
 * @returns {{ plainText: string, lines: string[], sorted: typeof blocks, stats: { blockCount: number } }}
 */
export function parseOcrBoxList(blocks, opts) {
    const yThreshold = (opts && opts.yThreshold) || 18
    const empty = { plainText: '', lines: [], sorted: [], stats: { blockCount: 0 } }
    if (!Array.isArray(blocks) || blocks.length === 0) return empty

    const items = blocks
        .filter((b) => b && typeof b.text === 'string' && String(b.text).trim() !== '')
        .map((b) => {
            const { x, y } = ocrBoxXY(b.box)
            return { raw: b, x, y }
        })

    items.sort((a, b) => a.y - b.y || a.x - b.x)

    /** @type {Array<{ items: typeof items }>} */
    const lineGroups = []
    for (const it of items) {
        let bestIdx = -1
        let bestDist = Infinity
        for (let i = 0; i < lineGroups.length; i++) {
            const g = lineGroups[i]
            const avgY = g.items.reduce((s, j) => s + j.y, 0) / g.items.length
            const d = Math.abs(it.y - avgY)
            if (d <= yThreshold && d < bestDist) {
                bestDist = d
                bestIdx = i
            }
        }
        if (bestIdx >= 0) lineGroups[bestIdx].items.push(it)
        else lineGroups.push({ items: [it] })
    }

    lineGroups.sort((a, b) => {
        const ay = a.items.reduce((s, i) => s + i.y, 0) / a.items.length
        const by = b.items.reduce((s, i) => s + i.y, 0) / b.items.length
        return ay - by
    })

    for (const g of lineGroups) {
        g.items.sort((a, b) => a.x - b.x)
    }

    const lines = lineGroups.map((g) =>
        g.items
            .map((i) => String(i.raw.text).trim())
            .filter(Boolean)
            .join(' ')
            .trim(),
    )
    const plainText = lines.filter(Boolean).join('\n')
    const sorted = items.map((i) => i.raw)

    return {
        plainText,
        lines: lines.filter(Boolean),
        sorted,
        stats: { blockCount: items.length },
    }
}

/**
 * 按 OCR 原始顺序用每条记录的 end（空格/换行）串联，不依赖坐标（与 parseOcrBoxList 互补）
 */
export function ocrBlocksToSequentialText(blocks) {
    if (!Array.isArray(blocks) || !blocks.length) return ''
    return blocks
        .filter((b) => b && typeof b.text === 'string')
        .map((b) => String(b.text) + (b.end != null ? String(b.end) : ''))
        .join('')
}

/**
 * 从 OCR HTTP 响应中取出纯文本（兼容 axios 包装与 Umi-OCR 等常见结构）
 */
export function extractOcrTextFromResponse(res) {
    const root = res && res.data
    if (root == null) return ''
    if (typeof root === 'string') return root
    if (typeof root !== 'object') return String(root)
    if (typeof root.data === 'string') return root.data
    if (root.data != null && typeof root.data.text === 'string') return root.data.text
    if (typeof root.text === 'string') return root.text
    if (Array.isArray(root.data)) {
        const arr = root.data
        const first = arr[0]
        if (first && typeof first === 'object' && Array.isArray(first.box)) {
            const { plainText } = parseOcrBoxList(arr)
            if (plainText) return plainText
        }
        const parts = arr.map((b) => (b && typeof b.text === 'string' ? b.text : '')).filter(Boolean)
        if (parts.length) return parts.join('\n')
    }
    return ''
}

/**
 * 按出现顺序提取脱敏账号（622845*******5217、621768****** 0026 等），去掉段内空格便于存储
 */
function extractOrderedMaskedAccounts(text) {
    const re = /(\d{5,8}\*+\d{4,6}|\d{5,8}\*+\s+\d{3,4})/g
    const seen = new Set()
    const out = []
    let m
    while ((m = re.exec(text)) !== null) {
        const tok = m[1].replace(/\s+/g, '')
        if (seen.has(tok)) continue
        seen.add(tok)
        out.push(tok)
    }
    return out
}

/** 农行等「交易详情」：支出/对方/付款账户字段 + 脱敏卡号，通常无付款户名 */
function isBankTransactionDetailOcr(text) {
    return (
        /支出金额|对方账户|对方名称|付款账户|交易流水|人行系统|账户余额|受理机构|交易状态|设备卡号/.test(
            text,
        ) && /\d{5,}\*{2,}/.test(text)
    )
}

/**
 * 从整段 OCR 文本中启发式提取打款账号、打款公司（户名）
 */
export function parsePayFromOcrText(text) {
    if (!text || typeof text !== 'string') {
        return { payaccount: '', payaccountname: '' }
    }
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const oneLine = normalized.replace(/\s+/g, ' ').trim()
    const isTxnDetail = isBankTransactionDetailOcr(normalized)

    let payaccount = ''
    let payaccountname = ''

    // 卡号(户名) 或 卡号（户名），半角/全角括号；括号内仅中文名，避免 [^)] 类里半角 ) 提前结束字符集
    const reCardName =
        /([\d][\d\s*±＊※*·.\-_＋+]{6,55})\s*[（(]\s*([\u4e00-\u9fa5·]{2,20})\s*[）)]/u

    // 单笔转账成功页：表头常连成「付款账号：付款金额：收款人：…」，不能与卡号紧邻，故先全局抓 卡号(中文名)
    let mMobile = normalized.match(
        /付款账号\s*[:：]\s*([\d\s*±＊※*·.\-_＋+]+)\s*[（(]\s*([\u4e00-\u9fa5·]{2,20})\s*[）)]/u,
    )
    if (!mMobile && /(?:单笔转账|转账成功)/.test(normalized)) {
        mMobile = normalized.match(reCardName)
    }
    if (!mMobile) {
        mMobile = normalized.match(reCardName)
    }
    if (mMobile) {
        const rawName = mMobile[2].trim()
        if (
            /^[\u4e00-\u9fa5·]{2,20}$/u.test(rawName) &&
            !/(?:有限公司|股份有限公司|支行|分行)/u.test(rawName)
        ) {
            payaccount = mMobile[1].replace(/\s+/g, '')
            payaccountname = rawName
        }
    }

    // 交易明细页：两条脱敏卡号按文序多为「对方(收款)」「付款账户」，无付款户名
    if (!payaccount && isTxnDetail) {
        const masked = extractOrderedMaskedAccounts(normalized)
        if (masked.length >= 2) payaccount = masked[1]
        else if (masked.length === 1) payaccount = masked[0]
    }

    // 银行客户回单：「付款人」区块内「名称」「账号」（区块止于首个「收款人」）
    const payerBlock = normalized.split(/\n?\s*收款人\s*[:：]?/u)[0]
    if (payerBlock && /(?:付款人|付款单位)/u.test(payerBlock)) {
        const nameM = payerBlock.match(
            /(?:付款人|付款单位)[\s\S]{0,500}?名称\s*[:：]\s*([^\n\r]+)/u,
        )
        const acctM = payerBlock.match(
            /(?:付款人|付款单位)[\s\S]{0,500}?账号\s*[:：]\s*([\d\s]+)/u,
        )
        if (!payaccountname && nameM && nameM[1]) {
            payaccountname = nameM[1]
                .replace(/\s+/g, ' ')
                .trim()
                .split(/\s{2,}/)[0]
        }
        if (!payaccount && acctM && acctM[1]) {
            const digits = acctM[1].replace(/\s+/g, '')
            if (/^\d{12,25}$/.test(digits)) payaccount = digits
        }
    }

    // 个人转账成功页：不要用「最长纯数字」当付款账号，否则会误选收款卡号
    const looksPersonalXfer =
        /(?:单笔转账|转账成功)/.test(normalized) ||
        /付款账号\s*[:：].*收款账号\s*[:：]/u.test(normalized)
    if (!payaccount && !looksPersonalXfer && !isTxnDetail) {
        const digitCandidates = oneLine.match(/\d[\d\s*\-]{14,28}\d|\d{16,22}/g)
        if (digitCandidates && digitCandidates.length) {
            const best = digitCandidates
                .map((s) => s.replace(/[\s*\-]/g, ''))
                .filter((s) => s.length >= 12 && s.length <= 25)
                .sort((a, b) => b.length - a.length)[0]
            if (best) payaccount = best
        }
    }

    if (!payaccountname && !isTxnDetail) {
        const labelRe =
            /(?:付款(?:账)?号?户名|付款人|付款单位|转出户名|户\s*名|账户名称|名称)[:：\s]*([^\n\d]{2,50})/u
        const m1 = normalized.match(labelRe)
        if (m1 && m1[1]) {
            payaccountname = m1[1].replace(/^[：:\s]+/, '').trim().split(/[\s\n]/)[0]
        }
    }
    if (!payaccountname && !isTxnDetail) {
        const m2 = oneLine.match(
            /([\u4e00-\u9fa5][\u4e00-\u9fa5·\s（）()]{1,55}(?:有限公司|股份有限公司|公司|商行|银行|合作社))/u,
        )
        if (m2) payaccountname = m2[1].trim()
    }

    return { payaccount, payaccountname }
}

/**
 * 将 OCR 中误用多个点分隔的金额规范为普通小数：最后一个点为小数位（须为两位），前面的点视为千分位。
 * 例：44.354.80 → 44354.80；22.590.40 → 22590.40；会先去掉逗号千分位。
 * @param {string|number} raw
 * @returns {string} 无法按此规则解析时返回去掉空白、逗号后的原串（或原样）
 */
export function normalizeDottedAmountString(raw) {
    if (raw == null) return ''
    let s = String(raw).trim().replace(/\s/g, '').replace(/,/g, '')
    if (!s || !s.includes('.')) return s
    const parts = s.split('.')
    if (parts.length < 2) return s
    const last = parts[parts.length - 1]
    if (!/^\d{2}$/.test(last)) return s
    const head = parts.slice(0, -1).join('')
    if (!/^\d+$/.test(head)) return s
    return `${head}.${last}`
}

/**
 * 从银行客户回单类 OCR 文本中提取付款人、收款人、金额（适配中信银行版式、PMB/RMB 误识）
 * @returns {{ payerName: string, payerAccount: string, payeeName: string, payeeAccount: string, amount: string }}
 * amount 为去掉千分位逗号后的数字串，如 22590.40；无法识别时为空串
 */
export function parseReceiptPartiesFromOcrText(text) {
    const empty = {
        payerName: '',
        payerAccount: '',
        payeeName: '',
        payeeAccount: '',
        amount: '',
    }
    if (!text || typeof text !== 'string') return empty

    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const base = parsePayFromOcrText(text)

    let payerName = ''
    let payerAccount = ''
    let payeeName = ''
    let payeeAccount = ''
    let amount = ''

    let bankDetailDone = false
    const isTxnDetail = isBankTransactionDetailOcr(normalized)

    // 农行/交易详情：金额+对方姓名一行；两条脱敏账号依次为收款、付款；无付款户名
    if (isTxnDetail) {
        const mAmtNm = normalized.match(
            /([\d，,]+\.\d{2})\s+([\u4e00-\u9fa5·]{2,20})(?=\s|$|\n)/u,
        )
        if (mAmtNm) {
            amount = mAmtNm[1].replace(/,/g, '').replace(/，/g, '')
            payeeName = mAmtNm[2].trim()
        }
        const masked = extractOrderedMaskedAccounts(normalized)
        if (masked.length >= 2) {
            payeeAccount = masked[0]
            payerAccount = masked[1]
        } else if (masked.length === 1 && mAmtNm) {
            payeeAccount = masked[0]
        } else if (masked.length === 1) {
            payerAccount = masked[0]
        }
        payerName = ''
        bankDetailDone = masked.length > 0 || !!mAmtNm
    }

    // 个人：单笔转账 / 转账成功 / 合并表头含「付款账号…收款账号」
    const looksPersonalXfer =
        /(?:单笔转账|转账成功)/.test(normalized) ||
        /付款账号\s*[:：].*收款账号\s*[:：]/u.test(normalized)
    if (looksPersonalXfer && !isTxnDetail) {
        const reCardName =
            /([\d][\d\s*±＊※*·.\-_＋+]{6,55})\s*[（(]\s*([\u4e00-\u9fa5·]{2,20})\s*[）)]/u
        const mCN = normalized.match(reCardName)
        if (mCN) {
            const nm = mCN[2].trim()
            if (
                /^[\u4e00-\u9fa5·]{2,20}$/u.test(nm) &&
                !/(?:有限公司|股份有限公司|支行|分行)/u.test(nm)
            ) {
                payerAccount = mCN[1].replace(/\s+/g, '')
                payerName = nm
            }
        }
        const mYenName = normalized.match(
            /[¥￥\uFFE5]\s*([\d，,]+\.\d{2})\s+([\u4e00-\u9fa5·]{2,20})(?:\s|$|\n)/u,
        )
        if (mYenName) {
            amount = mYenName[1].replace(/,/g, '').replace(/，/g, '')
            payeeName = mYenName[2].trim()
        }
        let rest = normalized
        if (mCN && payerAccount) rest = rest.replace(mCN[0], '')
        const mRecv =
            rest.match(
                /(?:收款账号\s*[:：]?\s*)?(\d{4}\s+\d{4}\s+\d{4}\s+\d{4}\s+\d{2,4})(?=\s|$|[^\d\s])/u,
            ) ||
            rest.match(/(\d{16,22})(?=\s*实时到账|\s*普通到账|\s*延迟)/u) ||
            rest.match(/(\d(?:\s*\d){15,21})(?=\s*实时到账|\s*普通到账|\s*延迟|\s|$)/u)
        if (mRecv) payeeAccount = mRecv[1].replace(/\s+/g, '')
    }

    // 金额：RMB / PMB(常见误识) / ¥ / ￥ / CNY（允许多点如 44.354.80，经 normalizeDottedAmountString 规整）
    if (!amount) {
        const amtM = normalized.match(/(?:RMB|PMB|CNY|[¥￥\uFFE5])\s*([\d.，,]+)/i)
        if (amtM) {
            amount = normalizeDottedAmountString(amtM[1].replace(/，/g, ','))
        }
    }

    // 「公司名称 + 空格 + 银行账号」成行出现两次 → 多为付款户+收款户（排除开户行支行行与短行号）
    const pairRe =
        /([\u4e00-\u9fa5·（）()]+(?:有限公司|股份有限公司|公司))\s+(\d{10,25})(?!\d)/g
    const pairs = []
    let pm
    while ((pm = pairRe.exec(normalized)) !== null) {
        const name = pm[1].trim()
        if (/(支行|分行|营业部)$/.test(name)) continue
        pairs.push({ name, account: pm[2] })
    }
    const seenAcc = new Set()
    const uniq = pairs.filter((p) => {
        if (seenAcc.has(p.account)) return false
        seenAcc.add(p.account)
        return true
    })

    if (uniq.length >= 2 && !bankDetailDone) {
        payerName = uniq[0].name
        payerAccount = uniq[0].account
        payeeName = uniq[1].name
        payeeAccount = uniq[1].account
        const digits = base.payaccount.replace(/\D/g, '')
        if (digits) {
            const i = uniq.findIndex((p) => p.account === digits)
            if (i === 1) {
                payerName = uniq[1].name
                payerAccount = uniq[1].account
                payeeName = uniq[0].name
                payeeAccount = uniq[0].account
            } else if (i === 0) {
                payerName = uniq[0].name
                payerAccount = uniq[0].account
                payeeName = uniq[1].name
                payeeAccount = uniq[1].account
            }
        }
    } else if (uniq.length === 1 && !bankDetailDone) {
        if (!payerName) payerName = uniq[0].name
        if (!payerAccount) payerAccount = uniq[0].account
    }

    // 中信银行两栏串行旧版式：付 名称 A 快 名称 B / 账号 账号 / 款 账号1 账号2
    const looksCitic =
        /客户回单|中信银行|CHINA\s*CITIC|CITIC\s*BANK/i.test(normalized)
    if (looksCitic && (!payeeName || !payeeAccount) && !bankDetailDone) {
        const mDualName = normalized.match(
            /付\s*名称\s+(.+?)\s+(?:快|收)款?\s*名称\s+([^\n\r]+)/u,
        )
        if (mDualName) {
            const takeCo = (s) => {
                const raw = String(s).replace(/\s+/g, ' ').trim()
                const co = raw.match(
                    /([\u4e00-\u9fa5·（）()]+\s*(?:有限公司|股份有限公司|公司|商行|银行|合作社))/u,
                )
                return co ? co[1].trim() : raw.split(/\s{2,}/)[0]
            }
            if (!payerName) payerName = takeCo(mDualName[1])
            if (!payeeName) payeeName = takeCo(mDualName[2])
        }
        const mDualAcct =
            normalized.match(
                /(?:账号\s+账号|账号\s*账号)[\s\n]*\s*款\s+(\d{10,25})\s+(\d{5,25})(?:\s|$|[^\d])/u,
            ) ||
            normalized.match(
                /(?:账号\s+账号|账号\s*账号)\s+款\s+(\d{10,25})\s+(\d{5,25})(?:\s|$|[^\d])/u,
            )
        if (mDualAcct) {
            if (!payerAccount) payerAccount = mDualAcct[1]
            if (!payeeAccount) payeeAccount = mDualAcct[2]
        }
    }

    if (!bankDetailDone && !payerName) payerName = (base.payaccountname || '').trim()
    if (!payerAccount) payerAccount = (base.payaccount || '').trim()

    return {
        payerName,
        payerAccount,
        payeeName,
        payeeAccount,
        amount,
    }
}



// WEBPACK FOOTER //
// ./src/util/ocrVoucherParse.js